import apiAgent from "@/app/api/apiAgent";
import { Button } from "@/app/common/common-components/ui/Button";
import {
  Dialog,
  DialogContent,
} from "@/app/common/common-components/ui/Dialog";
import { Heading } from "@/app/common/common-components/ui/Heading";
import { cn } from "@/app/common/common-functions/functions";
import NewLoader from "@/app/layout/NewLoader";
import { useQuery } from "@tanstack/react-query";
import {
  ChevronLeft,
  ChevronRight,
  Mail,
  MessageCircle,
  Phone,
} from "lucide-react";
import { useState } from "react";
import { useParams } from "react-router";

const toTitleCase = (str: string) => {
  return str
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const PhotoGridImage = ({
  url,
  onClick,
}: {
  url: string;
  onClick: () => void;
}) => {
  return (
    <div className="tw-h-full tw-w-full tw-cursor-pointer" onClick={onClick}>
      <div
        className="tw-flex tw-h-full tw-w-full tw-rounded-xl"
        style={{
          backgroundImage: `url(${url})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
    </div>
  );
};

type FullScreenPhotoSliderProps = {
  photos: { url: string }[];
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
};

const FullScreenPhotoSlider = ({
  photos,
  isOpen,
  onOpenChange,
}: FullScreenPhotoSliderProps) => {
  const [currentPhoto, setCurrentPhoto] = useState(0);
  const totalPhotos = photos.length;
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="tw-h-full tw-max-h-full tw-w-full tw-max-w-full tw-rounded-none">
        <div className="tw-hidden tw-h-full tw-w-full tw-items-center tw-justify-between tw-gap-6 md:tw-flex">
          <Button
            variant="outline"
            size="icon"
            disabled={currentPhoto === 0}
            onClick={() => setCurrentPhoto(currentPhoto - 1)}
          >
            <ChevronLeft className="tw-size-4" />
          </Button>
          <div
            className="tw-aspect-video tw-w-full tw-max-w-[80%]"
            style={{
              backgroundImage: `url(${photos[currentPhoto]?.url})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
          <Button
            variant="outline"
            size="icon"
            disabled={currentPhoto === totalPhotos - 1}
            onClick={() => setCurrentPhoto(currentPhoto + 1)}
          >
            <ChevronRight className="tw-size-4" />
          </Button>
        </div>

        <div className="tw-flex tw-h-full tw-w-full tw-flex-col tw-items-center tw-justify-between tw-gap-6 md:tw-hidden md:tw-flex-row">
          <div className="tw-flex tw-h-full tw-w-full tw-flex-1 tw-flex-col tw-items-center tw-justify-center">
            <div
              className="tw-aspect-video tw-w-full"
              style={{
                backgroundImage: `url(${photos[currentPhoto]?.url})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            />
          </div>
          <div className="tw-flex tw-h-fit tw-w-full tw-justify-center tw-gap-6">
            <Button
              variant="outline"
              size="icon"
              disabled={currentPhoto === 0}
              onClick={() => setCurrentPhoto(currentPhoto - 1)}
            >
              <ChevronLeft className="tw-size-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              disabled={currentPhoto === totalPhotos - 1}
              onClick={() => setCurrentPhoto(currentPhoto + 1)}
            >
              <ChevronRight className="tw-size-4" />
            </Button>
          </div>
        </div>
        <div className="tw-mx-auto tw-text-sm tw-text-muted-foreground">
          {currentPhoto + 1} of {totalPhotos}
        </div>
      </DialogContent>
    </Dialog>
  );
};

type PhotoGridProps = {
  photos: {
    url: string;
  }[];
  onPhotoClick: () => void;
};

const PhotoGrid = ({ photos, onPhotoClick }: PhotoGridProps) => {
  const threeOrMore = photos.filter((p) => !!p.url).length >= 3;
  return (
    <div
      className={cn(
        "tw-grid tw-h-96 tw-grid-cols-1 tw-gap-6",
        threeOrMore && "md:tw-grid-cols-12"
      )}
    >
      <div className="tw-col-span-8 tw-flex tw-flex-col tw-gap-6">
        <PhotoGridImage url={photos[0]?.url} onClick={onPhotoClick} />
      </div>
      {threeOrMore && (
        <div className="tw-col-span-4 tw-hidden tw-flex-col tw-gap-6 md:tw-flex">
          <PhotoGridImage url={photos[1]?.url} onClick={onPhotoClick} />
          <PhotoGridImage url={photos[2]?.url} onClick={onPhotoClick} />
        </div>
      )}
    </div>
  );
};

const FeatureItem = ({ label, value }: { label: string; value?: string }) => {
  if (typeof value === undefined || value?.length === 0) {
    return null;
  }

  const valueString = value?.toString() || "-";

  const labelString = toTitleCase(label?.toString() || "-");

  return (
    <div className="tw-flex tw-justify-between tw-border-b-2 tw-border-gray-200 tw-py-2">
      <span>{labelString}</span>
      <span className="tw-font-bold">{valueString}</span>
    </div>
  );
};

const PublicListingPage = () => {
  const { id } = useParams();
  const { data, status } = useQuery({
    queryKey: ["public-listing", id],
    queryFn: () => apiAgent.Public.getPublicListing(id!),
    enabled: !!id,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
    retry: false,
  });

  const [isFullScreenPhotoSliderOpen, setIsFullScreenPhotoSliderOpen] =
    useState(false);

  if (status === "pending") {
    return <NewLoader />;
  }

  if (status === "error" || !id || !data) {
    return (
      <div className="tw-flex tw-h-screen tw-w-full tw-items-center tw-justify-center">
        <Heading asChild level={2}>
          <span>404 Not Found</span>
        </Heading>
      </div>
    );
  }

  console.log(data);

  return (
    <>
      <FullScreenPhotoSlider
        photos={data.photos
          .filter((p) => !!p.url_large)
          .map((p) => ({ url: p.url_large }))}
        isOpen={isFullScreenPhotoSliderOpen}
        onOpenChange={setIsFullScreenPhotoSliderOpen}
      />
      <div className="tw-flex tw-flex-col tw-p-6">
        <div className="tw-mx-auto tw-flex tw-min-h-10 tw-w-full tw-max-w-screen-lg tw-flex-col tw-gap-6">
          <div className="tw-flex tw-flex-col-reverse tw-justify-between md:tw-flex-row">
            <div className="tw-flex tw-flex-col">
              <Heading asChild>
                <span>{data.headline}</span>
              </Heading>
              <div className="tw-text-lg tw-text-muted-foreground">
                {data.location_suburb}, {data.location_town}
              </div>
            </div>
            <div className="tw-text-lg tw-font-bold">
              Ref: #{data.property_id}
            </div>
          </div>

          <PhotoGrid
            photos={data.photos.map((p) => ({ url: p.url_large }))}
            onPhotoClick={() => setIsFullScreenPhotoSliderOpen(true)}
          />

          <div className="tw-grid tw-grid-cols-1 tw-gap-6 md:tw-grid-cols-12">
            <div className="tw-flex tw-flex-col tw-gap-4 md:tw-col-span-8">
              <Heading asChild level={2}>
                {data.listing_price > 0 ? (
                  <span>
                    {data.listing_currency} {data.listing_price}
                  </span>
                ) : (
                  <span>POA</span>
                )}
              </Heading>

              <div className="tw-flex tw-flex-wrap tw-gap-2">
                {data.features
                  .filter((feature) =>
                    ["true", "false"].includes(feature.value.toLowerCase())
                  )
                  .map((feature) => (
                    <div
                      key={feature.field}
                      className="tw-text-nowrap tw-rounded-sm tw-bg-slate-900 tw-px-2 tw-py-1 tw-text-white"
                    >
                      {toTitleCase(feature.field)}
                    </div>
                  ))}
              </div>

              <div className="tw-grid tw-grid-cols-2 tw-gap-x-6">
                <FeatureItem label="Property Type" value={data.property_type} />
                {data.rates_taxes && (
                  <FeatureItem
                    label="Rates & Taxes"
                    value={`${data.listing_currency} ${data.rates_taxes} ${data.rates_taxes_unit}`}
                  />
                )}
                {data.land_size && (
                  <FeatureItem
                    label="Land Size"
                    value={`${data.land_size} ${data.land_size_unit}`}
                  />
                )}
                {data.floor_size && (
                  <FeatureItem
                    label="Floor Size"
                    value={`${data.floor_size} ${data.floor_size_unit}`}
                  />
                )}
                {data.features
                  .filter(
                    (feature) =>
                      !["true", "false"].includes(feature.value.toLowerCase())
                  )
                  .map((feature) => (
                    <FeatureItem
                      key={feature.field}
                      label={feature.field}
                      value={feature.value}
                    />
                  ))}
              </div>

              <div>
                {data.description.split("\n").map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>
            </div>

            <div className="tw-flex tw-flex-col tw-gap-6 md:tw-col-span-4">
              {data.agents.map((agent) => (
                <div
                  key={agent.agent_id}
                  className="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-rounded-xl tw-bg-white tw-p-4"
                >
                  <img
                    src={agent.photo_url}
                    alt={agent.first_name}
                    className="tw-w-24"
                  />
                  <Heading level={4} asChild>
                    <span>
                      {agent.first_name} {agent.last_name}
                    </span>
                  </Heading>
                  <div className="tw-flex tw-justify-center tw-gap-2">
                    <div className="tw-flex tw-flex-col tw-items-center tw-gap-1">
                      <a
                        href={`tel:${agent.mobile_number}`}
                        className="tw-rounded-sm tw-border tw-border-color1 tw-p-2 tw-text-color1 hover:tw-bg-slate-100 hover:tw-text-color1"
                      >
                        <Phone className="tw-size-4" />
                      </a>
                      <span className="tw-text-xs">Call</span>
                    </div>
                    <div className="tw-flex tw-flex-col tw-items-center tw-gap-1">
                      <a
                        href={`mailto:${agent.email}`}
                        className="tw-rounded-sm tw-border tw-border-color1 tw-p-2 tw-text-color1 hover:tw-bg-slate-100 hover:tw-text-color1"
                      >
                        <Mail className="tw-size-4" />
                      </a>
                      <span className="tw-text-xs">Email</span>
                    </div>
                    <div className="tw-flex tw-flex-col tw-items-center tw-gap-1">
                      <a
                        href={`https://wa.me/${agent.mobile_number}`}
                        className="tw-rounded-sm tw-border tw-border-color1 tw-p-2 tw-text-color1 hover:tw-bg-slate-100 hover:tw-text-color1"
                      >
                        <MessageCircle className="tw-size-4" />
                      </a>
                      <span className="tw-text-xs">Chat</span>
                    </div>
                  </div>
                </div>
              ))}

              <div className="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-rounded-xl tw-bg-white tw-p-4">
                <img
                  src={data.office.logo_url}
                  alt={data.office.office_name}
                  className="tw-max-w-[60%]"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PublicListingPage;
